import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface MetaTagData {
  title: string;
  description: string;
  image: string;
  url?: string;
  type?: string;
  siteName?: string;
  keywords?: string;
  // Music-specific meta tags
  musicDuration?: number;
  musicAlbum?: string;
  musicMusician?: string;
  // Twitter-specific
  twitterCard?: string;
}

interface MetaTagManagerProps {
  data?: MetaTagData;
  children?: React.ReactNode;
}

const MetaTagManager: React.FC<MetaTagManagerProps> = ({ data, children }) => {
  const location = useLocation();

  useEffect(() => {
    if (!data) return;

    const currentUrl = `https://lsnto.com${location.pathname}${location.search}`;
    const canonicalUrl = data.url || (currentUrl.split('?')[0] || currentUrl);
    
    // Ensure image URLs are absolute
    const absoluteImage = data.image && data.image.startsWith('/')
      ? `https://lsnto.com${data.image}`
      : data.image;
    
    // Update document title
    document.title = data.title;
    
    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;

    // Helper function to update or create meta tag
    const updateMetaTag = (property: string, content: string, isName = false) => {
      const selector = isName ? `meta[name="${property}"]` : `meta[property="${property}"]`;
      let tag = document.querySelector(selector) as HTMLMetaElement;
      
      if (tag) {
        tag.content = content;
      } else {
        tag = document.createElement('meta');
        if (isName) {
          tag.name = property;
        } else {
          tag.setAttribute('property', property);
        }
        tag.content = content;
        document.head.appendChild(tag);
      }
    };

    // Update Open Graph tags
    updateMetaTag('og:title', data.title);
    updateMetaTag('og:description', data.description);
    updateMetaTag('og:image', absoluteImage);
    updateMetaTag('og:url', data.url || currentUrl);
    updateMetaTag('og:type', data.type || 'website');
    updateMetaTag('og:site_name', data.siteName || 'Lsnto');
    updateMetaTag('og:locale', 'en_GB');

    // Music-specific Open Graph tags
    if (data.musicDuration) {
      updateMetaTag('music:duration', data.musicDuration.toString());
    }
    if (data.musicAlbum) {
      updateMetaTag('music:album', data.musicAlbum);
    }
    if (data.musicMusician) {
      updateMetaTag('music:musician', data.musicMusician);
    }

    // Update Twitter Card tags
    updateMetaTag('twitter:card', data.twitterCard || 'summary_large_image', true);
    updateMetaTag('twitter:title', data.title, true);
    updateMetaTag('twitter:description', data.description, true);
    updateMetaTag('twitter:image', absoluteImage, true);
    updateMetaTag('twitter:site', '@lsntomusic', true);

    // Update standard meta tags
    updateMetaTag('description', data.description, true);
    if (data.keywords) {
      updateMetaTag('keywords', data.keywords, true);
    }

    // Add structured data for better SEO
    const structuredData = {
      "@context": "https://schema.org",
      "@type": data.type === 'music.song' ? 'MusicRecording' : 
               data.type === 'music.album' ? 'MusicAlbum' : 
               data.type === 'profile' ? 'MusicGroup' : 'WebPage',
      "name": data.title,
      "description": data.description,
      "image": absoluteImage,
      "url": data.url || currentUrl,
      ...(data.musicMusician && { "byArtist": { "@type": "MusicGroup", "name": data.musicMusician } }),
      ...(data.musicAlbum && { "inAlbum": { "@type": "MusicAlbum", "name": data.musicAlbum } }),
      ...(data.musicDuration && { "duration": `PT${data.musicDuration}S` })
    };

    // Update or create structured data script
    let structuredDataScript = document.querySelector('script[type="application/ld+json"][data-meta-manager]') as HTMLScriptElement;
    if (structuredDataScript) {
      structuredDataScript.textContent = JSON.stringify(structuredData);
    } else {
      structuredDataScript = document.createElement('script');
      structuredDataScript.type = 'application/ld+json';
      structuredDataScript.setAttribute('data-meta-manager', 'true');
      structuredDataScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(structuredDataScript);
    }

  }, [data, location]);

  return <>{children}</>;
};

// Hook for easy meta tag updates
export const useMetaTags = (data: MetaTagData) => {
  useEffect(() => {
    const metaManager = document.createElement('div');
    const root = document.createElement('div');
    metaManager.appendChild(root);
    
    // This will trigger the meta tag updates
    const component = React.createElement(MetaTagManager, { data });
    
    return () => {
      // Cleanup if needed
    };
  }, [data]);
};

// Utility functions for creating meta data
export const createArtistMetaData = (artist: {
  name: string;
  bio?: string;
  image_url?: string;
  slug?: string;
}): MetaTagData => ({
  title: `${artist.name} – Lsnto | What Do You Want to Lsnto?`,
  description: artist.bio || `Discover ${artist.name} on Lsnto – the artist-first music streaming platform. Stream their music, explore their discography, and connect directly.`,
  image: artist.image_url || 'https://lsnto.com/favicon/web-app-manifest-512x512.png',
  type: 'profile',
  musicMusician: artist.name,
  keywords: `${artist.name}, Lsnto, independent music, stream music, artist`,
});

export const createAlbumMetaData = (album: {
  title: string;
  artist_name?: string;
  artwork_url?: string;
  description?: string;
  release_date?: string;
  tracks?: any[];
}, artist?: { name: string }): MetaTagData => {
  const artistName = album.artist_name || artist?.name || 'Unknown Artist';
  const releaseYear = album.release_date ? new Date(album.release_date).getFullYear() : '';
  const trackCount = album.tracks ? album.tracks.length : 0;
  const trackText = trackCount === 1 ? 'track' : 'tracks';
  
  const description = album.description || 
    `Listen to ${album.title} by ${artistName} on Lsnto.${releaseYear ? ` Released ${releaseYear}.` : ''}${trackCount > 0 ? ` ${trackCount} ${trackText}.` : ''}`;
  
  return {
    title: `${album.title} by ${artistName} – Lsnto | What Do You Want to Lsnto?`,
    description: description,
    image: album.artwork_url || 'https://lsnto.com/favicon/web-app-manifest-512x512.png',
    type: 'music.album',
    musicAlbum: album.title,
    musicMusician: artistName,
    keywords: `${album.title}, ${artistName}, Lsnto, album, independent music, stream`,
  };
};

export const createTrackMetaData = (track: {
  title: string;
  duration?: number;
  albumName?: string;
  artistName?: string;
  albumArtwork?: string;
}): MetaTagData => ({
  title: `${track.title} by ${track.artistName} – Lsnto | What Do You Want to Lsnto?`,
  description: `Listen to ${track.title} by ${track.artistName}${track.albumName ? ` from ${track.albumName}` : ''} on Lsnto – the artist-first independent music streaming platform.`,
  image: track.albumArtwork || 'https://lsnto.com/favicon/web-app-manifest-512x512.png',
  type: 'music.song',
  musicDuration: track.duration,
  musicAlbum: track.albumName,
  musicMusician: track.artistName,
  keywords: `${track.title}, ${track.artistName || ''}, Lsnto, music, stream`,
});

export const createHomePageMetaData = (): MetaTagData => ({
  title: "Lsnto – Music Revolution | The Future of Music is Here",
  description: "Join Lsnto – the future of music listening! Music chosen by you and made for you, not served by algorithms. An artist-first platform with fair artist compensation. Connect directly with artists and discover authentic music experiences.",
  image: "https://lsnto.com/favicon/web-app-manifest-512x512.png",
  url: "https://lsnto.com",
  type: "website",
  siteName: "Lsnto",
  twitterCard: "summary_large_image",
  keywords: "Lsnto, music streaming, independent music, artist-first, fair pay artists, music revolution, stream music, indie music, discover music",
});

export const createJoinPageMetaData = (): MetaTagData => ({
  title: "Join Lsnto as an Artist – The Artist-First Music Platform",
  description: "Lsnto is built by artists, for artists. Get 80% royalties, fan-powered discovery, and innovative release tools. Join the artist waitlist and take back control of your music career.",
  image: "https://lsnto.com/favicon/web-app-manifest-512x512.png",
  url: "https://lsnto.com/join",
  type: "website",
  siteName: "Lsnto",
  keywords: "Lsnto artist, join Lsnto, music artist platform, independent artist, fair royalties, artist waitlist",
});

export const createFanWaitlistMetaData = (): MetaTagData => ({
  title: "Join the Lsnto Fan Waitlist – Discover Independent Music",
  description: "Sign up to Lsnto and be among the first to experience a music platform built for real fans. No algorithms, no major-label bias – just authentic music discovery and direct artist connections.",
  image: "https://lsnto.com/favicon/web-app-manifest-512x512.png",
  url: "https://lsnto.com/fan-waitlist",
  type: "website",
  siteName: "Lsnto",
  keywords: "Lsnto fan waitlist, music fan, discover indie music, independent music platform, music discovery",
});

export default MetaTagManager; 